import React, { useState } from 'react';
import { Card, CardHeader, IconButton, CardContent, Typography, Stack, Box, Popover } from '@mui/material';
import { SentimentSatisfiedAlt, Help } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import SentimentGauge from '../charts/SentimentGauge';
import { useTheme } from '@mui/material/styles';
import ValueVersusValue from '../ValueVersusValue';
import RenderPeriodDateRange from '../../account/reportingPeriod/RenderPeriodDateRange';

const Weighting = () => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'sentiment-weighting' : undefined;

    return (
        <>
            <IconButton onClick={handleClick} color="info">
                <Help />
            </IconButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Box p={2} sx={{ maxWidth: 550 }}>
                    <Typography variant="body1" fontWeight={500} sx={{ pb: 0.5 }}>Weighting</Typography>
                    <Typography variant="body2" color={theme.palette.text.secondary}>Your Sentiment score is the sum total of the individual scores calculated from the percentage values for the average Unique Open Rate, average Unique Click Rate, average Total Open Rate, and average Total Click Rate. Each score is arrived at using the weighting shown below.</Typography>
                    <Stack direction="row" spacing={3} sx={{ pt: 1 }}>
                        <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ pb: 0.5 }}>Unique Open Rate</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>0 - 10% = 0.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>11 - 20% = 1</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>21 - 40% = 1.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>41 - 60% = 2</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>61 - 80% = 2.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>81 - 100% = 3</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ pb: 0.5 }}>Total Open Rate</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>0 - 50% = 0.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>51 - 100% = 1</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>101 - 200% = 1.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>Over 200% = 2</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ pb: 0.5 }}>Unique Click Rate</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>0 - 5% = 0.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>6 - 10% = 1</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>11 - 20% = 1.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>21 - 50% = 2</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>51 - 80% = 2.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>81 - 100% = 3</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ pb: 0.5 }}>Total Click Rate</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>0 - 10% = 0.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>11 - 40% = 1</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>41 - 100% = 1.5</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>Over 100% = 2</Typography>
                        </Box>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
};

const SentimentCard = ({ a, b, aColour, bColour, selectedPeriods }) => {
    const theme = useTheme();

    return (
        <Card sx={{ height: '100%', m: 0, p: 0 }}>
            <CardHeader
                sx={{ m: 0, py: 1 }}
                title="Sentiment"
                slotProps={{
                    title: {
                        variant: 'h6',
                        fontWeight: 'normal'
                    }
                }}
                avatar={<SentimentSatisfiedAlt />}
                action={<Weighting />}
            />
            <CardContent
                sx={{
                    m: 0,
                    px: 2,
                    py: 0,
                    '&:last-child': {
                        paddingBottom: 2
                    }
                }}
            >
                <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                    {!b && (
                        <Grid sx={{ textAlign: 'center' }} size={12}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                <RenderPeriodDateRange
                                    period={selectedPeriods ? selectedPeriods[0] : null}
                                />
                            </Typography>
                        </Grid>
                    )}
                    <Grid size={12}>
                        <SentimentGauge
                            score={a.sentimentScore}
                            colour={aColour}
                            period={b ? 'A' : null}
                        />
                    </Grid>
                    <Grid size={12}>
                        {b ? (
                            <SentimentGauge
                                score={b.sentimentScore}
                                colour={bColour}
                                period="B"
                            />
                        ) : (
                            <Box>
                                <Grid container spacing={1}>
                                    <Grid sx={{ textAlign: 'center' }} size={12}>
                                        <Typography variant="body1">Score Breakdown</Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <ValueVersusValue
                                            a={a.sentimentBreakdown?.openRateScore || 0}
                                            text="Unique Open Rate"
                                            aColour={aColour}
                                            bColour={bColour}
                                            accuracy={1}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <ValueVersusValue
                                            a={a.sentimentBreakdown?.totalOpensScore || 0}
                                            text="Total Open Rate"
                                            aColour={aColour}
                                            bColour={bColour}
                                            accuracy={1}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <ValueVersusValue
                                            a={a.sentimentBreakdown?.clickRateScore || 0}
                                            text="Unique Click Rate"
                                            aColour={aColour}
                                            bColour={bColour}
                                            accuracy={1}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <ValueVersusValue
                                            a={a.sentimentBreakdown?.totalClicksScore || 0}
                                            text="Total Click Rate"
                                            aColour={aColour}
                                            bColour={bColour}
                                            accuracy={1}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Grid>
                    {b && (
                        <>
                            <Grid sx={{ textAlign: 'center', pt: 2 }} size={12}>
                                <Typography variant="body1">Score Breakdown</Typography>
                            </Grid>
                            <Grid size={6}>
                                <ValueVersusValue
                                    a={a.sentimentBreakdown?.openRateScore || 0}
                                    b={b.sentimentBreakdown?.openRateScore || 0}
                                    text="Unique Open Rate"
                                    aColour={aColour}
                                    bColour={bColour}
                                    accuracy={1}
                                />
                            </Grid>
                            <Grid size={6}>
                                <ValueVersusValue
                                    a={a.sentimentBreakdown?.totalOpensScore || 0}
                                    b={b.sentimentBreakdown?.totalOpensScore || 0}
                                    text="Total Open Rate"
                                    aColour={aColour}
                                    bColour={bColour}
                                    accuracy={1}
                                />
                            </Grid>
                            <Grid size={6}>
                                <ValueVersusValue
                                    a={a.sentimentBreakdown?.clickRateScore || 0}
                                    b={b.sentimentBreakdown?.clickRateScore || 0}
                                    text="Unique Click Rate"
                                    aColour={aColour}
                                    bColour={bColour}
                                    accuracy={1}
                                />
                            </Grid>
                            <Grid size={6}>
                                <ValueVersusValue
                                    a={a.sentimentBreakdown?.totalClicksScore || 0}
                                    b={b.sentimentBreakdown?.totalClicksScore || 0}
                                    text="Total Click Rate"
                                    aColour={aColour}
                                    bColour={bColour}
                                    accuracy={1}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SentimentCard;